import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { 
  Users, Calendar, Plus, Trash2, Shield, ArrowRight, 
  Search, Filter, Clock, CheckCircle, XCircle, User 
} from 'lucide-react';
import Button from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import Combobox from '../components/ui/Combobox';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { motion, AnimatePresence } from 'framer-motion';

const DelegationManager = () => {
  const { user } = useAuth();
  const [delegations, setDelegations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    onConfirm: () => {}
  });
  
  // Form State
  const [formData, setFormData] = useState({
    delegateId: '',
    startDate: '',
    endDate: '',
    permissions: 'Full'
  });
  
  // Hierarchy State for User Selection
  const [hierarchy, setHierarchy] = useState([]);
  const [users, setUsers] = useState([]); // All users in office to filter from
  const [selectedWing, setSelectedWing] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    fetchDelegations();
    fetchHierarchy();
    fetchUsers();
  }, []);

  useEffect(() => {
    // Filter users based on hierarchy selection
    if (!users.length) return;
    
    let result = users.filter(u => u.id !== user?.id); // Exclude self
    
    if (selectedWing) result = result.filter(u => u.wingId == selectedWing);
    if (selectedDept) result = result.filter(u => u.departmentId == selectedDept);
    if (selectedSection) result = result.filter(u => u.sectionId == selectedSection);
    
    setFilteredUsers(result);
  }, [users, selectedWing, selectedDept, selectedSection, user?.id]);

  const fetchDelegations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/delegations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setDelegations(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch (error) {
      console.error('Error fetching delegations:', error);
      toast.error('Failed to load delegations');
    } finally {
      setLoading(false);
    }
  };

  const fetchHierarchy = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/offices/organization/tree', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setHierarchy(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching hierarchy:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch all users in the office to populate the dropdown
      // Using a simplified endpoint or existing one
      const response = await axios.get('/api/users?limit=1000', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        // response.data.data is the array of users directly from getAllUsers
        setUsers(response.data.data);
        setFilteredUsers(response.data.data.filter(u => u.id !== user?.id));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateDelegation = async (e) => {
    e.preventDefault();
    if (!formData.delegateId || !formData.startDate || !formData.endDate) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/delegations', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Delegation created successfully');
        setShowModal(false);
        setFormData({ delegateId: '', startDate: '', endDate: '', permissions: 'Full' });
        fetchDelegations();
      }
    } catch (error) {
      console.error('Error creating delegation:', error);
      toast.error(error.response?.data?.message || 'Failed to create delegation');
    }
  };

  const handleRevoke = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Revoke Delegation',
      message: 'Are you sure you want to revoke this delegation? This will immediately remove access rights.',
      confirmText: 'Revoke',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`/api/delegations/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Delegation revoked successfully');
          fetchDelegations();
        } catch (error) {
          console.error('Error revoking:', error);
          toast.error('Failed to revoke delegation');
        }
      }
    });
  };

  return (
    <>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Delegation Management</h1>
          <p className="text-gray-500 mt-1">Delegate your authority to other officers during leave or absence.</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg">
          <Plus className="w-5 h-5 mr-2" /> New Delegation
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {(!delegations || delegations.length === 0) ? (
            <div className="p-12 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No active delegations found.</p>
              <p className="text-sm mt-1">Create a new delegation to assign your duties.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Delegate</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Period</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Permissions</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {delegations && delegations.map((delegation) => (
                    <tr key={delegation.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold mr-3">
                            {delegation.delegateName?.charAt(0) || 'U'}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{delegation.delegateName}</div>
                            <div className="text-xs text-gray-500">{delegation.delegateDesignation || 'Officer'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          <span>{format(new Date(delegation.startDate), 'MMM dd, yyyy')}</span>
                          <ArrowRight className="w-4 h-4 mx-2 text-gray-400" />
                          <span>{format(new Date(delegation.endDate), 'MMM dd, yyyy')}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {delegation.permissions}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center w-fit gap-1 ${
                          delegation.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {delegation.status === 'Active' ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                          {delegation.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleRevoke(delegation.id)}
                          className="text-red-600 hover:text-red-900 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Revoke
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create Delegation Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">New Delegation</h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleCreateDelegation} className="space-y-4">
                {/* User Selection Hierarchy */}
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700">Select Delegate</label>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <Combobox 
                      value={selectedWing} 
                      onChange={val => {
                        setSelectedWing(val);
                        setSelectedDept('');
                        setSelectedSection('');
                      }}
                      className="text-sm"
                      options={[
                        { value: "", label: "All Wings" },
                        ...hierarchy.map(w => ({ value: w.id, label: w.name }))
                      ]}
                      placeholder="Select Wing"
                    />

                    <Combobox 
                      value={selectedDept} 
                      onChange={val => {
                        setSelectedDept(val);
                        setSelectedSection('');
                      }} 
                      disabled={!selectedWing}
                      className="text-sm"
                      options={[
                        { value: "", label: "All Departments" },
                        ...(selectedWing && hierarchy.find(w => w.id == selectedWing)?.departments.map(d => ({ value: d.id, label: d.name })) || [])
                      ]}
                      placeholder="Select Department"
                    />
                  </div>

                  <Combobox 
                    value={selectedSection} 
                    onChange={val => setSelectedSection(val)} 
                    disabled={!selectedDept}
                    className="text-sm"
                    options={[
                      { value: "", label: "All Sections" },
                      ...(selectedDept && hierarchy.find(w => w.id == selectedWing)?.departments.find(d => d.id == selectedDept)?.sections.map(s => ({ value: s.id, label: s.name })) || [])
                    ]}
                    placeholder="Select Section"
                  />

                  <Combobox 
                    name="delegateId"
                    value={formData.delegateId} 
                    onChange={(val) => setFormData({...formData, delegateId: val})}
                    required
                    className="font-medium"
                    options={[
                      { value: "", label: "Select Officer..." },
                      ...filteredUsers.map(u => ({ value: u.id, label: `${u.name} - ${u.designation || u.role}` }))
                    ]}
                    placeholder="Select User to Delegate"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Input 
                    type="date" 
                    label="Start Date" 
                    value={formData.startDate} 
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    required 
                  />
                  <Input 
                    type="date" 
                    label="End Date" 
                    value={formData.endDate} 
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    required 
                  />
                </div>

                <div className="pt-4 flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white">
                    Create Delegation
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={() => {
          confirmDialog.onConfirm();
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        }}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
      />
    </>
  );
};

export default DelegationManager;
